import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NgbOffcanvas, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { AppSettings } from '../../shared/app-settings';
import { QuickCaptureDrawerComponent } from './quick-capture-drawer/quick-capture-drawer.component';

/**
 * Global Quick Capture singleton. Listens for Ctrl+Alt+N (or Cmd+Alt+N on Mac)
 * and opens the capture drawer. Single-instance — opening when already open
 * closes the drawer (toggle behaviour).
 *
 * Instantiated at app startup via injection into AppComponent so the keydown
 * listener is wired regardless of which route the user is on.
 */
@Injectable({ providedIn: 'root' })
export class QuickCaptureService implements OnDestroy {
  private currentRef: NgbOffcanvasRef | null = null;
  private listenerAttached = false;

  constructor(
    private offcanvas: NgbOffcanvas,
    private zone: NgZone,
  ) {
    this.attachHotkey();
  }

  ngOnDestroy(): void {
    if (this.listenerAttached) {
      window.removeEventListener('keydown', this.onKeydown, true);
      this.listenerAttached = false;
    }
  }

  /** Open the drawer; closes any existing instance first. */
  open(): NgbOffcanvasRef {
    if (this.currentRef) {
      this.currentRef.close();
      this.currentRef = null;
    }

    const ref = this.offcanvas.open(QuickCaptureDrawerComponent, {
      position: 'end',
      panelClass: 'drawer-sm',
      backdrop: true,
      keyboard: true,
      scroll: false,
      ariaLabelledBy: 'quick-capture-title',
    });

    this.currentRef = ref;
    ref.result
      .catch(() => undefined) // dismiss = rejected — swallow so it isn't logged as an unhandled error
      .finally(() => {
        if (this.currentRef === ref) this.currentRef = null;
      });

    return ref;
  }

  /** Toggle: open if closed, close if open. Silently no-ops when not logged in. */
  toggle(): void {
    if (this.currentRef) {
      this.currentRef.close();
      this.currentRef = null;
      return;
    }
    if (!this.isLoggedIn()) return;
    this.open();
  }

  /** Quick check — drawer needs a logged-in user for the picker API calls to work. */
  private isLoggedIn(): boolean {
    try {
      return !!localStorage.getItem(AppSettings.StorageKeys.User);
    } catch {
      return false;
    }
  }

  private attachHotkey(): void {
    if (this.listenerAttached || typeof window === 'undefined') return;
    // Capture phase + outside Angular zone — keeps this off the change-detection critical path.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('keydown', this.onKeydown, true);
    });
    this.listenerAttached = true;
  }

  private onKeydown = (event: KeyboardEvent): void => {
    // Ctrl+Alt+N on Windows/Linux, Cmd+Alt+N on Mac.
    const isHotkey = (event.ctrlKey || event.metaKey) && event.altKey && event.key.toLowerCase() === 'n';
    if (!isHotkey) return;

    // Skip when typing in a code editor — it likely owns the same chord.
    const target = event.target as HTMLElement | null;
    if (target?.closest('.cm-editor, .monaco-editor, [contenteditable="true"], [contenteditable=""]')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.zone.run(() => this.toggle());
  };
}
